import { getCookie } from '@/features/shared/utils/cookies'
import {
  SHIPT_RESTRICTION_SERVER_COOKIE,
  SHIPT_PRIVACY_BROWSER_COOKIE,
  SHIPT_PRIVACY_COOKIE_VALUE,
  SHIPT_PRIVACY_SERVER_COOKIE,
  SHIPT_RESTRICTION_BROWSER_COOKIE,
} from '@shared/constants/dataRights'
import { isOnServer } from '@shared/constants/util'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { getUser } from '@/features/account/services/User/utils'
import {
  type DataRightsSignUpValues,
  type DataRightsTrait,
  type RegulationCategoryStateAndReason,
} from '@/features/account/services/DataPrivacy/types'
import { type RequestCookies } from '@shared/request_type'
import { type QueryClient } from '@tanstack/react-query'
import { type ValidAddress } from '@/features/account/services/Addresses/types'
import { INFERRED_ZIP } from '@shared/constants/zip'
import { isValidZip } from '@/features/shared/utils/validator'
import { getGuestStoreParamsSSR } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { getGuestParamsFromCache } from '@/features/account/services/GuestUser/getGuestParamsFromCache'

type UserIsDoNotShareSellTAParams = {
  cookies: RequestCookies | undefined
  queryClient: QueryClient | undefined
}

export const optOutBrowserSignalIsActive = () =>
  !isOnServer() && window.navigator.globalPrivacyControl

export const optOutBrowserCookieIsActive = (cookies?: RequestCookies) => {
  if (isOnServer()) {
    return cookies?.[SHIPT_PRIVACY_SERVER_COOKIE] === SHIPT_PRIVACY_COOKIE_VALUE
  }
  return Boolean(getCookie(SHIPT_PRIVACY_BROWSER_COOKIE))
}

export const optOutRestrictionsCookieIsActive = (cookies?: RequestCookies) => {
  if (isOnServer()) {
    return Boolean(cookies?.[SHIPT_RESTRICTION_SERVER_COOKIE])
  }
  return Boolean(getCookie(SHIPT_RESTRICTION_BROWSER_COOKIE))
}

export const isOptOutCookieOrSignalActive = (cookies?: RequestCookies) =>
  optOutBrowserCookieIsActive(cookies) ||
  optOutBrowserSignalIsActive() ||
  optOutRestrictionsCookieIsActive(cookies)

export const userIsDoNotShareSellTA = (params?: UserIsDoNotShareSellTAParams): boolean => {
  const requestCookies = params?.cookies

  if (isOptOutCookieOrSignalActive(requestCookies)) return true

  const user = getUser(params?.queryClient ?? getQueryClient())

  // do_not_share_sell_ta is the source of truth for logged in users if it is true.
  if (user?.do_not_share_sell_ta) return true

  // a valid address id means a logged in user's opt out status was already
  // verified via the restrictions query. we can safely skip the zip heuristic.
  if (user?.default_shopping_address_id) return false

  return isOptOutByZipHeuristic(requestCookies)
}

const isOptOutByZipHeuristic = (cookies: RequestCookies | undefined): boolean => {
  const guestAddress: ValidAddress | undefined = isOnServer()
    ? getGuestStoreParamsSSR(cookies ?? {}).guestAddress
    : getGuestParamsFromCache().guestAddress

  // if a guest has provided a full street address, their opt out status is handled
  // by the restrictions service. we do not need to rely on the zip code heuristic.
  if (guestAddress?.street1) return false

  const fastlyZip: string | undefined = isOnServer()
    ? cookies?.[INFERRED_ZIP]
    : (getCookie(INFERRED_ZIP) ?? undefined)
  const zipToCheck = guestAddress?.zip_code ?? fastlyZip ?? ''
  const zip = isValidZip(zipToCheck) ? zipToCheck : undefined

  /**
   * fallback Heuristic:
   * 1. no zip: we can't verify location, so we assume the user is opted out by default.
   * 2. zip present: check if it belongs to Washington, our only restricted state.
   *
   * note: if the API returns a different status than the heuristic, the corrected opt
   * out cookies will be set accordingly and the user will be considered opted in or out
   * as needed on the next request. this is fine as we want to ensure privacy compliance
   * even in edge cases where the heuristic for missing zip code might be incorrect.
   */
  return zip ? isWashingtonZip(zip) : true
}

export const getDataRightsTrait = (params?: UserIsDoNotShareSellTAParams): DataRightsTrait => ({
  do_not_share_sell_ta: userIsDoNotShareSellTA(params),
})

export const getDataRightsSignUpValues = (): DataRightsSignUpValues => {
  if (optOutBrowserSignalIsActive()) {
    return {
      do_not_share_sell_ta: true,
      do_not_share_sell_ta_source: 'web-signal',
    }
  }
  if (optOutBrowserCookieIsActive()) {
    return {
      do_not_share_sell_ta: true,
      do_not_share_sell_ta_source: 'web-form',
    }
  }
  if (optOutRestrictionsCookieIsActive()) {
    const restrictionCookieValues: RegulationCategoryStateAndReason =
      getCookie(SHIPT_RESTRICTION_BROWSER_COOKIE) || {}

    return {
      do_not_share_sell_ta: true,
      do_not_share_sell_ta_source: 'web-restriction',
      ...restrictionCookieValues,
    }
  }
  return {}
}

// checks if a zip code falls within the Washington state zip code range
// matches BE logic, see https://github.com/shipt/dsr-broker/blob/fdfdcc563c7228401b258f5ced82f717b548e22a/internal/webserver/dsr/privacy/handler.go#L241
const isWashingtonZip = (zip: string | number): boolean => {
  const zipStr = String(zip)
  if (!isValidZip(zipStr)) return false

  const intZip = parseInt(zipStr.substring(0, 5), 10)
  if (isNaN(intZip)) return false

  return intZip >= 98001 && intZip <= 99403
}
